import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { css } from "@emotion/react";
import { stringify } from "qs";
import { decamelizeKeys } from "humps";
import { CardImage } from "main/javascripts/components/card/CardImage";
import { Cruise } from "main/javascripts/types/cruise";
import { space } from "main/javascripts/styles/base/spaceStyle";
import {
  fontSize,
  fontWeight,
  letterSpacing,
  lineHeight,
} from "main/javascripts/styles/base/typographyStyle";
import {
  accentColor,
  textColor,
} from "main/javascripts/styles/base/colorStyle";
import { moreThanBreakpoint } from "main/javascripts/styles/base/responsiveStyle";
import { borderRadius } from "main/javascripts/styles/base/borderStyle";
import { nl2br } from "main/javascripts/utils/StringUtil";
import { LINE_ENTRY_LIFF_URL } from "main/javascripts/constants/Constants";
import { getLineEntryValues } from "main/javascripts/utils/trackingUtil";
import { useAppSelector } from "main/javascripts/store";
import { isTrackingParameterStoredSelector } from "main/javascripts/slices/paramSlice";
import { isFetchedAccountParamSelector } from "main/javascripts/slices/accountParamSlice";
import { CruiseShowParam } from "main/javascripts/types/cruiseShowParam";
import { AButton } from "../button/AButton";
import { GoogleTagManagerEventTypes } from "main/javascripts/constants/GoogleTagManagerEventTypes";

export interface IProps {
  cabinRate: Cruise.VesselCabinRate;
  param: CruiseShowParam;
}

export const CruiseCabinCardItem: React.FC<IProps> = (
  props: IProps
): React.ReactElement => {
  const { cabinRate, param } = props;

  const { t } = useTranslation([
    "component",
    "label",
    "unit",
    "button",
    "constant",
  ]);

  const emailConsultationBasePath = `/mail_cruise_consultations/${cabinRate.cruiseUniqueId}/new`;

  const isTrackingParameterStored = useAppSelector(
    isTrackingParameterStoredSelector
  );
  const isFetchedAccountParam = useAppSelector(isFetchedAccountParamSelector);

  const [lineEntryUrl, setLineEntryUrl] = useState(LINE_ENTRY_LIFF_URL);
  const [emailConsultationPath, setEmailConsultationPath] = useState(
    emailConsultationBasePath
  );

  useEffect(() => {
    if (isTrackingParameterStored || isFetchedAccountParam) {
      const lineEntryValues = getLineEntryValues();
      const values = decamelizeKeys(param);
      const query: { [key: string]: any } = {
        cruise_id: cabinRate.cruiseUniqueId,
        vessel_cabin_code: cabinRate.cabinCode,
        departure_date: values.departure_date,
        guests: values.guests,
        ...lineEntryValues,
      };
      const queryString = stringify(query);
      setLineEntryUrl(`${LINE_ENTRY_LIFF_URL}?${queryString}`);
      setEmailConsultationPath(`${emailConsultationBasePath}?${queryString}`);
    }
  }, [isTrackingParameterStored, isFetchedAccountParam]);

  const cabin = cabinRate.vesselCabin;
  const rate = cabinRate.rate;
  const guestNum = rate?.adults + rate?.children || 0;
  const hasSalePrice = rate?.hasSalePrice;

  return (
    <div css={cardItemStyle}>
      {hasSalePrice && (
        <div css={salePriceLabelStyle}>{t("component:cruise.salePrice")}</div>
      )}
      <div css={imageBlockStyle}>
        <div css={imageStyle}>
          <CardImage url={cabin.imageUrl} />
        </div>
      </div>
      <div css={infoBlockStyle}>
        <div css={titleStyle}>{cabin.name}</div>
        {cabin.description && (
          <div css={descriptionStyle}>{nl2br(cabin.description)}</div>
        )}
        <div css={dataBlockStyle}>
          <div>
            {t("label:cruise.cabinCode")}: {cabin.cabinCode}
          </div>
        </div>
        {rate ? (
          <div css={priceBlockStyle}>
            {rate.saleText && <div css={saleTextStyle}>{rate.saleText}</div>}
            <div css={priceStyle}>
              {rate.price}
              <span css={priceNoteStyle}>
                {t("unit:person", { count: guestNum })} /{" "}
                {t("cruise.includeTaxAndFee")}
              </span>
            </div>
            <div css={buttonBlockStyle}>
              <div css={buttonStyle}>
                <AButton
                  href={emailConsultationPath}
                  type="button"
                  styleKey="buttonSmall"
                  fontSize={fontSize.caption}
                  paddingStyle="0"
                  data-event-category={
                    GoogleTagManagerEventTypes.clickCruiseConsultation
                  }
                >
                  {t("button:common.contactViaEmail")}
                </AButton>
              </div>
              <div css={buttonStyle}>
                <AButton
                  href={lineEntryUrl}
                  type="button"
                  styleKey="buttonSmallLine"
                  fontSize={fontSize.caption}
                  paddingStyle="0"
                  data-event-category={
                    GoogleTagManagerEventTypes.clickCruiseConsultation
                  }
                >
                  {t("button:common.contactViaLine")}
                </AButton>
              </div>
            </div>
          </div>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

const cardItemStyle = css`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  flex: 1;
`;
const salePriceLabelStyle = css`
  position: absolute;
  top: ${space.atom2x};
  left: ${space.atom2x};
  background-color: ${textColor.secondaryAccentColor};
  color: #fff;
  font-size: ${fontSize.caption};
  font-weight: ${fontWeight.medium};
  line-height: ${lineHeight.caption2};
  letter-spacing: ${letterSpacing.text};
  padding: 0.125rem ${space.atom};
  ${moreThanBreakpoint("tablet")} {
    top: ${space.atom2_5x};
    left: ${space.atom2_5x};
  }
`;
const imageBlockStyle = css`
  width: 100%;
  box-sizing: border-box;
  padding: ${space.atom2x} ${space.atom2x} ${space.atom};
  ${moreThanBreakpoint("tablet")} {
    height: auto;
    width: 38%;
    padding: ${space.atom2_5x};
    padding-right: ${space.atom};
  }
`;
const imageStyle = css`
  aspect-ratio: 2 / 1;
  overflow: hidden;
  border-radius: ${borderRadius.normal};
  ${moreThanBreakpoint("tablet")} {
    aspect-ratio: 3 / 2;
  }
`;
const infoBlockStyle = css`
  width: 100%;
  box-sizing: border-box;
  padding: ${space.atom} ${space.atom};
  ${moreThanBreakpoint("tablet")} {
    width: 62%;
    padding: ${space.atom2x} ${space.atom1_5x};
  }
`;
const titleStyle = css`
  font-size: ${fontSize.headline};
  line-height: ${lineHeight.headline1};
  font-weight: ${fontWeight.medium};
  color: ${textColor.primaryDarkColor};
  padding: ${space.atom0_5x} ${space.atom} ${space.atom};
  ${moreThanBreakpoint("desktop")} {
    padding: ${space.atom0_5x} 0;
  }
`;
const descriptionStyle = css`
  font-size: ${fontSize.caption};
  line-height: ${lineHeight.caption2};
  color: ${textColor.secondaryDarkColor};
  padding: ${space.atom0_5x} ${space.atom};
  ${moreThanBreakpoint("desktop")} {
    padding: ${space.atom0_5x} 0;
  }
`;
const dataBlockStyle = css`
  padding: 0 ${space.atom} ${space.atom0_5x};
  font-size: ${fontSize.caption};
  line-height: ${lineHeight.caption2};
  color: ${textColor.secondaryDarkColor};
  ${moreThanBreakpoint("desktop")} {
    padding: 0 0 ${space.atom0_5x};
  }
`;
const priceBlockStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding: ${space.atom} ${space.atom};
`;
const priceStyle = css`
  color: ${textColor.primaryDarkColor};
  font-size: 1.5rem;
  font-weight: ${fontWeight.bold};
  text-align: right;
  font-variant-numeric: tabular-nums;
  line-height: ${lineHeight.noSpace};
  white-space: nowrap;
  ${moreThanBreakpoint("desktop")} {
    font-size: 1.5rem;
  }
`;
const priceNoteStyle = css`
  display: block;
  font-weight: ${fontWeight.regular};
  font-size: 0.75rem;
  line-height: ${lineHeight.caption3};
  color: ${textColor.secondaryDarkColor};
  opacity: 0.75;
  margin-top: 2px;
`;
const saleTextStyle = css`
  width: 100%;
  font-size: ${fontSize.body};
  line-height: ${lineHeight.body2};
  color: ${accentColor.primaryColor};
  font-weight: ${fontWeight.medium};
  text-align: right;
`;
const buttonBlockStyle = css`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: ${space.atom};
  padding-top: ${space.atom};
  ${moreThanBreakpoint("tablet")} {
    gap: ${space.atom1_5x};
    min-width: 19rem;
  }
`;
const buttonStyle = css`
  width: 8.5rem;
  display: flex;
`;
